import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { buildScopeTranslate } from "lib/I18n";
import { PREVIEW_TYPES } from "../constants";
import { refPropType } from "../utils";
import { gradingErrorMessage, isHeldWait, retryWaitSeconds } from "../gradingError";

const SCOPE = "views.content.assessment_framework.assessments.pages.error";
const tPage = buildScopeTranslate(SCOPE);

// A preview keeps nothing on the server, and what Retry does depends on the kind: an author
// preview posts the same answers again, a learner preview starts over with a new draw.
function paragraphText(previewType, held) {
  if (held) {
    return tPage("paragraph_held");
  }

  if (previewType === PREVIEW_TYPES.AUTHOR) {
    return tPage("paragraph_preview_author");
  }

  return previewType === PREVIEW_TYPES.LEARNER ? tPage("paragraph_preview_learner") : tPage("paragraph");
}

function retryLabel(previewType) {
  if (previewType === PREVIEW_TYPES.AUTHOR) {
    return tPage("buttons.retry_preview_author");
  }

  return previewType === PREVIEW_TYPES.LEARNER ? tPage("buttons.retry_preview_learner") : tPage("buttons.retry_grading");
}

// The header of the results page for an SAQ attempt whose grading failed: what went wrong
// and what the learner can do, with the answers listed beneath it by the results screen.
// It does not share markup with the generic ErrorScreen, so each can be restyled alone.
// onRetry is whatever Retry does for this attempt (null when there is none); while its
// wait runs the button stays disabled.
const GradingErrorScreen = ({ gradingError, previewType = null, retrying = false, onRetry = null, onBackToTrail, headingRef = null }) => {
  const waitSeconds = retryWaitSeconds(gradingError);
  const waiting = waitSeconds > 0;

  // The wait is not shown, so assistive tech needs to hear once that it is over.
  const [retryReady, setRetryReady] = useState(false);
  const previousWait = useRef(waitSeconds);
  useEffect(() => {
    if (waitSeconds > 0) {
      setRetryReady(false);
    } else if (previousWait.current > 0) {
      setRetryReady(true);
    }
    previousWait.current = waitSeconds;
  }, [waitSeconds]);

  const message = gradingErrorMessage(gradingError);
  const held = isHeldWait(gradingError);

  return (
    <div className="slds-text-align_center th-agentforce-assessment__grading-error-heading" data-testid="error-screen">
      <div role="alert" className="th-sr-only">
        {message}
      </div>
      <h1
        ref={headingRef}
        className="th-agentforce-assessment__result-heading"
        data-testid="error-heading-default"
        tabIndex="-1">
        {tPage("grading_title")}
      </h1>
      {/* One block, so the header's gap does not come between the message and the line below it. */}
      <div>
        <p
          className="slds-text-body_regular slds-m-around_none th-agentforce-assessment__subtitle"
          data-testid="error-message-custom">
          {message}
        </p>
        {(onRetry || held) && (
          <p
            className="slds-text-body_regular slds-m-around_none slds-text-color_weak"
            data-testid="error-subtitle-default">
            {paragraphText(previewType, held)}
          </p>
        )}
      </div>
      {onRetry && (
        <div role="status" className="th-sr-only" data-testid="error-retry-ready">
          {retryReady ? tPage("retry_ready") : null}
        </div>
      )}
      <div className="slds-grid slds-grid_align-center slds-gutters_small">
        {!onRetry && !held && (
          <div className="slds-col">
            <tds-button variant="neutral" onClick={onBackToTrail} data-testid="error-back-btn">
              <span>{tPage("buttons.back")}</span>
            </tds-button>
          </div>
        )}
        {onRetry && (
          <div className="slds-col">
            <tds-button variant="brand" icon-symbol="refresh" onClick={onRetry} disabled={waiting || retrying} data-testid="error-retry-btn">
              <span>{retryLabel(previewType)}</span>
            </tds-button>
          </div>
        )}
      </div>
    </div>
  );
};

GradingErrorScreen.propTypes = {
  gradingError: PropTypes.shape({
    code: PropTypes.string,
    retry: PropTypes.shape({ href: PropTypes.string }),
    retryAvailableAt: PropTypes.number,
  }).isRequired,
  // Which preview this is, if any: it changes what Retry does, so what the screen says about it.
  previewType: PropTypes.oneOf(Object.values(PREVIEW_TYPES)),
  // A retry was just asked for and has not been answered yet.
  retrying: PropTypes.bool,
  onRetry: PropTypes.func,
  onBackToTrail: PropTypes.func.isRequired,
  headingRef: refPropType,
};

export default GradingErrorScreen;
